import React, { useState, useRef, useEffect, useCallback } from "react";
import Cropper from "react-easy-crop";

// API servisleri
const API_BASE_URL = "https://europe-west3-bruh-bbadb.cloudfunctions.net";

// ✅ Reklam banner ölçüleri
const PREVIEW_DEVICE_WIDTH_PX = 540;
const LEFT_MENU_WIDTH_PX = 144;
const RIGHT_CONTENT_PADDING_X_PX = 16;
const AD_BANNER_HEIGHT_PX = 88;
const AD_BANNER_WIDTH_PX = PREVIEW_DEVICE_WIDTH_PX - LEFT_MENU_WIDTH_PX - RIGHT_CONTENT_PADDING_X_PX;
const AD_BANNER_ASPECT = AD_BANNER_WIDTH_PX / AD_BANNER_HEIGHT_PX;

// Token & Card
const getAuthToken = () => localStorage.getItem("userToken");
const getAuthCard = () => localStorage.getItem("card");

const redirectToLogin = () => {
  localStorage.removeItem("userToken");
  localStorage.removeItem("card");
  window.location.href = "/mlogin";
};

const checkAuthentication = () => {
  const token = getAuthToken();
  const card = getAuthCard();
  if (!token || !card) {
    redirectToLogin();
    return false;
  }
  return true;
};

const makeApiRequest = async (url, options = {}) => {
  if (!checkAuthentication()) {
    throw new Error("Authentication failed. Redirecting to login.");
  }

  const token = getAuthToken();
  const card = getAuthCard();

  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let requestBody = options.body;
  if (requestBody && card) {
    try {
      const bodyData = JSON.parse(requestBody);
      if (!bodyData.card) {
        bodyData.card = card;
        requestBody = JSON.stringify(bodyData);
      }
    } catch (_) {}
  }

  const config = { ...options, headers, body: requestBody };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      if (errorData.redirectToLogin) {
        redirectToLogin();
        throw new Error("Authentication failed. Redirecting to login.");
      }

      if (response.status === 401 || response.status === 403) {
        redirectToLogin();
        throw new Error("Authentication failed. Redirecting to login.");
      }

      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if (
      (error.message || "").includes("Authentication failed") ||
      (error.message || "").includes("token") ||
      (error.message || "").includes("card")
    ) {
      redirectToLogin();
    }
    throw error;
  }
};

// ✅ R2 Upload (Backend üzerinden)
const R2_UPLOAD_ENDPOINT = `${API_BASE_URL}/uploadimagetor2`;

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (e) => reject(e);
    reader.readAsDataURL(blob);
  });

const uploadFileToR2ViaBackend = async (fileOrBlob, fileName, folder = "asdasd") => {
  try {
    if (!checkAuthentication()) {
      throw new Error("Authentication failed. Redirecting to login.");
    }

    const card = getAuthCard();
    const contentType = fileOrBlob?.type || "application/octet-stream";

    const dataUrl = await blobToDataUrl(fileOrBlob);

    const splitDataUrl = (dataUrl) => {
      const s = String(dataUrl || "");
      const comma = s.indexOf(",");
      if (comma === -1) return { base64: s, mime: "" };
      const meta = s.slice(0, comma);
      const base64 = s.slice(comma + 1);
      const mimeMatch = meta.match(/data:(.*?);base64/i);
      return { base64, mime: mimeMatch ? mimeMatch[1] : "" };
    };
    
    const { base64, mime } = splitDataUrl(dataUrl);

    const payload = {
      card,
      fileName,
      folder,
      contentType: contentType || mime || "application/octet-stream",
      dataUrl,
      base64,
    };

    const resp = await makeApiRequest(R2_UPLOAD_ENDPOINT, {
      method: "POST",
      body: JSON.stringify(payload),
    });

    const url =
      resp?.url ||
      resp?.publicUrl ||
      resp?.downloadURL ||
      resp?.imageUrl ||
      resp?.result?.url;

    if (!resp?.success || !url) {
      throw new Error(resp?.message || "R2 upload başarısız (backend url döndürmedi).");
    }

    return url;
  } catch (error) {
    throw error;
  }
};

const emojiMap = {
  coffee: "☕",
  tea: "🍵",
  bubble_tea: "🧋",
  juice: "🧃",
  soda: "🥤",
  espresso: "☕",
  cappuccino: "☕",
  latte: "☕",
  mocha: "☕",
  cold_brew: "🥤",
  water: "💧",
  wine: "🍷",
  beer: "🍺",
  cocktail: "🍹",
  croissant: "🥐",
  sandwich: "🥪",
  burger: "🍔",
  pizza: "🍕",
  salad: "🥗",
  sushi: "🍣",
  taco: "🌮",
  burrito: "🌯",
  ramen: "🍜",
  spaghetti: "🍝",
  soup: "🍲",
  stew: "🥘",
  cake: "🍰",
  cupcake: "🧁",
  ice_cream: "🍦",
  doughnut: "🍩",
  cookie: "🍪",
  pie: "🥧",
  chocolate: "🍫",
  candy: "🍬",
  pudding: "🍮",
  pancake: "🥞",
  waffle: "🧇",
  egg: "🍳",
  bacon: "🥓",
  bread: "🍞",
  bagel: "🥯",
  drinks: "🥤",
  desserts: "🍰",
  breakfast: "🥞",
  lunch: "🍽️",
  dinner: "🍲",
  snacks: "🍪",
  specials: "⭐",
  popular: "🔥",
  new: "🆕",
  vegan: "🌱",
  default: "📦",
};

const getEmojiFromCode = (emojiCode) => {
  if (!emojiCode) return emojiMap.default;
  const code = String(emojiCode).trim();
  return emojiMap[code] || emojiMap.default;
};

const emojiOptions = [
  { code: "coffee", emoji: "☕", label: "Coffee" },
  { code: "tea", emoji: "🍵", label: "Tea" },
  { code: "bubble_tea", emoji: "🧋", label: "Bubble Tea" },
  { code: "juice", emoji: "🧃", label: "Juice" },
  { code: "soda", emoji: "🥤", label: "Soda" },
  { code: "water", emoji: "💧", label: "Water" },
  { code: "wine", emoji: "🍷", label: "Wine" },
  { code: "beer", emoji: "🍺", label: "Beer" },
  { code: "cocktail", emoji: "🍹", label: "Cocktail" },
  { code: "croissant", emoji: "🥐", label: "Croissant" },
  { code: "sandwich", emoji: "🥪", label: "Sandwich" },
  { code: "burger", emoji: "🍔", label: "Burger" },
  { code: "pizza", emoji: "🍕", label: "Pizza" },
  { code: "salad", emoji: "🥗", label: "Salad" },
  { code: "sushi", emoji: "🍣", label: "Sushi" },
  { code: "taco", emoji: "🌮", label: "Taco" },
  { code: "burrito", emoji: "🌯", label: "Burrito" },
  { code: "ramen", emoji: "🍜", label: "Ramen" },
  { code: "spaghetti", emoji: "🍝", label: "Spaghetti" },
  { code: "cake", emoji: "🍰", label: "Cake" },
  { code: "cupcake", emoji: "🧁", label: "Cupcake" },
  { code: "ice_cream", emoji: "🍦", label: "Ice Cream" },
  { code: "doughnut", emoji: "🍩", label: "Doughnut" },
  { code: "cookie", emoji: "🍪", label: "Cookie" },
  { code: "pancake", emoji: "🥞", label: "Pancake" },
  { code: "waffle", emoji: "🧇", label: "Waffle" },
  { code: "egg", emoji: "🍳", label: "Egg" },
  { code: "bacon", emoji: "🥓", label: "Bacon" },
  { code: "bread", emoji: "🍞", label: "Bread" },
  { code: "specials", emoji: "⭐", label: "Specials" },
  { code: "popular", emoji: "🔥", label: "Popular" },
  { code: "new", emoji: "🆕", label: "New" },
  { code: "vegan", emoji: "🌱", label: "Vegan" },
];

const LoadingSpinner = ({ message = "LOADING..." }) => {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center text-black">
        <div className="w-48 h-48 border-8 border-black border-t-transparent rounded-full animate-spin mx-auto mb-8"></div>
        <p className="text-3xl font-bold mb-4">{message}</p>
      </div>
    </div>
  );
};

const colorMap = {
  red: "from-red-500 to-red-600",
  blue: "from-blue-500 to-blue-600",
  green: "from-green-500 to-green-600",
  yellow: "from-yellow-500 to-yellow-600",
  orange: "from-orange-500 to-orange-600",
  purple: "from-purple-500 to-purple-600",
  pink: "from-pink-500 to-pink-600",
  indigo: "from-indigo-500 to-indigo-600",
  teal: "from-teal-500 to-teal-600",
  cyan: "from-cyan-500 to-cyan-600",
  amber: "from-amber-500 to-amber-600",
  emerald: "from-emerald-500 to-emerald-600",
  lime: "from-lime-500 to-lime-600",
  rose: "from-rose-500 to-rose-600",
  fuchsia: "from-fuchsia-500 to-fuchsia-600",
  violet: "from-violet-500 to-violet-600",
  sunset: "from-orange-500 to-pink-500",
  ocean: "from-blue-500 to-teal-500",
  forest: "from-green-500 to-emerald-500",
  berry: "from-purple-500 to-pink-500",
  fire: "from-red-500 to-orange-500",
  sky: "from-cyan-500 to-blue-500",
  default: "from-amber-500 to-orange-500",
};

const headerColorMap = {
  red: "linear-gradient(to right, #dc2626, #ea580c, #dc2626)",
  blue: "linear-gradient(to right, #2563eb, #1d4ed8, #2563eb)",
  green: "linear-gradient(to right, #059669, #0d9488, #10b981)",
  yellow: "linear-gradient(to right, #eab308, #ca8a04, #eab308)",
  orange: "linear-gradient(to right, #ea580c, #d97706, #ea580c)",
  purple: "linear-gradient(to right, #7c3aed, #6d28d9, #7c3aed)",
  pink: "linear-gradient(to right, #db2777, #be185d, #db2777)",
  indigo: "linear-gradient(to right, #4f46e5, #4338ca, #4f46e5)",
  teal: "linear-gradient(to right, #0d9488, #0f766e, #0d9488)",
  cyan: "linear-gradient(to right, #0891b2, #0e7490, #0891b2)",
  emerald: "linear-gradient(to right, #059669, #047857, #059669)",
  default: "linear-gradient(to right, #059669, #0d9488, #10b981)",
};

const getColorClass = (colorName) => {
  if (!colorName) return colorMap.default;
  const color = String(colorName).trim().toLowerCase();
  return colorMap[color] || colorMap.default;
};

const getHeaderColor = (colorName) => {
  if (!colorName) return headerColorMap.default;
  const color = String(colorName).trim().toLowerCase();
  return headerColorMap[color] || headerColorMap.default;
};

const SaveLoadingModal = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
      <div className="text-center">
        <div className="w-20 h-20 border-8 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <h3 className="text-2xl font-bold text-gray-800 mb-2">Saving Design</h3>
        <p className="text-gray-600">Your design is being saved to the backend...</p>
      </div>
    </div>
  </div>
);

const SaveSuccessModal = ({ onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
      <div className="text-center">
        <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-gray-800 mb-2">Success!</h3>
        <p className="text-gray-600 mb-6">Your content has been successfully saved to the backend.</p>
        <button
          onClick={onClose}
          className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors w-full"
        >
          OK
        </button>
      </div>
    </div>
  </div>
);

const UnsavedChangesModal = ({ onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
      <div className="text-center">
        <div className="w-20 h-20 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-gray-800 mb-2">Unsaved Changes</h3>
        <p className="text-gray-600 mb-6">You have unsaved changes. Do you want to proceed without saving?</p>
        <div className="flex space-x-4">
          <button
            onClick={onCancel}
            className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            No
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Yes
          </button>
        </div>
      </div>
    </div>
  </div>
);

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const getCroppedBlob = async (imageSrc, pixelCrop, quality = 0.92) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"));
          return;
        }
        resolve(blob);
      },
      "image/jpeg",
      quality
    );
  });
};

const CropModal = ({ isOpen, imageSrc, aspect = 9 / 16, onClose, onConfirm, title = "Crop Image" }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    }
  }, [isOpen]);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white w-full max-w-4xl rounded-2xl overflow-hidden shadow-2xl">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold"
          >
            Close
          </button>
        </div>

        <div className="p-6">
          <div className="relative w-full h-[520px] bg-gray-900 rounded-xl overflow-hidden">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>

          <div className="mt-6 flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Zoom</label>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1"
            />
            <div className="text-sm text-gray-500 w-12 text-right">{zoom.toFixed(2)}x</div>
          </div>

          <div className="mt-8 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-6 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold"
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm(croppedAreaPixels)}
              disabled={!croppedAreaPixels}
              className="px-8 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold disabled:bg-gray-300 disabled:text-gray-500"
            >
              Use Cropped Image
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const HomePagePreview = ({ designData }) => {
  const firstEmoji = getEmojiFromCode(designData.firstemo);
  const secondEmoji = getEmojiFromCode(designData.secondemo);
  const thirdEmoji = getEmojiFromCode(designData.thirdemo);

  const getOrderButtonClass = () => `bg-gradient-to-r ${getColorClass(designData.butonrenk)}`;

  return (
    <div className="h-[960px] w-[540px] relative overflow-hidden bg-black">
      {designData.appvideo ? (
        <img
          src={designData.appvideo}
          alt="Background"
          className="absolute top-0 left-0 w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-black to-gray-900" />
      )}

      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>

      <div className="absolute top-16 left-1/2 transform -translate-x-1/2 z-10">
        <div className="bg-black/30 backdrop-blur-md rounded-2xl p-4 border border-white/20">
          <img
            src={designData.applogo || `${process.env.PUBLIC_URL}/cafe-de-colombia-logo-png-transparent.png`}
            alt="App Logo"
            className="h-20 w-auto"
          />
        </div>
      </div>

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
        <h1 className="text-6xl font-bold text-white mb-4 drop-shadow-2xl leading-tight">
          {designData.appyazi || "Cafe de Colombia"}
        </h1>

        <p className="text-2xl text-white/90 font-light mb-12 drop-shadow-lg tracking-wide">
          {designData.appaltyazi || "Artisan Coffee Experience"}
        </p>

        <div
          className={`${getOrderButtonClass()} px-20 py-6 text-white text-3xl font-bold rounded-2xl shadow-2xl hover:shadow-3xl border-2 border-white/20 transition-all duration-300 transform hover:scale-105 cursor-pointer group relative overflow-hidden`}
        >
          <div className="absolute inset-0 bg-white/10 group-hover:bg-white/20 transition-all duration-300"></div>
          <span className="relative z-10">ORDER NOW</span>
        </div>

        <div className="flex space-x-6 mt-16">
          <div className="text-center group cursor-pointer">
            <div className="w-20 h-20 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 flex items-center justify-center mb-3 group-hover:bg-white/20 transition-all duration-300">
              <span className="text-4xl text-white">{firstEmoji}</span>
            </div>
            <p className="text-white/80 text-sm font-medium">{designData.firstemowrite || "Coffee"}</p>
          </div>

          <div className="text-center group cursor-pointer">
            <div className="w-20 h-20 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 flex items-center justify-center mb-3 group-hover:bg-white/20 transition-all duration-300">
              <span className="text-4xl text-white">{secondEmoji}</span>
            </div>
            <p className="text-white/80 text-sm font-medium">{designData.secondemowrite || "Drinks"}</p>
          </div>

          <div className="text-center group cursor-pointer">
            <div className="w-20 h-20 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 flex items-center justify-center mb-3 group-hover:bg-white/20 transition-all duration-300">
              <span className="text-4xl text-white">{thirdEmoji}</span>
            </div>
            <p className="text-white/80 text-sm font-medium">{designData.thirdemowrite || "Desserts"}</p>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-8 right-8 bg-black/30 backdrop-blur-md rounded-2xl p-4 border border-white/20">
        <div className="flex justify-between items-center text-white/80">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
          </div>
          <div className="text-sm">
            <span className="font-medium">{designData.openhour || "8:00 AM - 10:00 PM"}</span>
          </div>
          <div className="text-sm">
            <span className="font-medium">{designData.addinfo || "Since 1995"}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const TakeawayPagePreview = ({ designData }) => {
  const [selectedOption, setSelectedOption] = useState("drinkIn");
  const [isPulsing, setIsPulsing] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => setIsPulsing((prev) => !prev), 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="h-[960px] w-[540px] bg-white relative overflow-hidden">
      <div className="absolute top-[60px] left-1/2 transform -translate-x-1/2 text-center">
        <div className="bg-green-100 backdrop-blur-sm rounded-2xl p-6 mb-4 border border-green-300">
          <p className="text-3xl text-black whitespace-nowrap font-bold px-6 py-4 rounded-xl">
            Please Select Your Order Type
          </p>
        </div>
      </div>

      <div className="absolute left-[20px] top-1/2 transform -translate-y-1/2 text-center">
        <div
          className={`bg-green-50 backdrop-blur-sm rounded-2xl p-4 mb-4 border-4 transition-all duration-500 cursor-pointer hover:scale-105 w-48 ${
            selectedOption === "drinkIn"
              ? `border-4 ${isPulsing ? "border-green-600" : "border-green-400"} bg-green-100 shadow-2xl scale-110`
              : "border-green-300 hover:border-green-500"
          }`}
          onClick={() => setSelectedOption("drinkIn")}
        >
          <img
            src={
              designData.drinkin ||
              "https://img.freepik.com/premium-vector/hand-holding-paper-cup_22669-107.jpg?semt=ais_hybrid&w=740&q=80"
            }
            alt="Drink In"
            className="h-32 w-32 object-cover mx-auto"
          />
          <div className="mt-4 w-full">
            <p className="text-2xl text-black font-bold">{designData.drinkinwrite || "Drink In"}</p>
          </div>
        </div>
      </div>

      <div className="absolute right-[20px] top-1/2 transform -translate-y-1/2 text-center">
        <div
          className={`bg-green-50 backdrop-blur-md rounded-2xl p-4 mb-4 border-4 transition-all duration-500 cursor-pointer hover:scale-105 w-48 ${
            selectedOption === "takeAway"
              ? `border-4 ${isPulsing ? "border-green-600" : "border-green-400"} bg-green-100 shadow-2xl scale-110`
              : "border-green-300 hover:border-green-500"
          }`}
          onClick={() => setSelectedOption("takeAway")}
        >
          <img
            src={
              designData.takeaway ||
              "https://static.vecteezy.com/system/resources/previews/014/585/858/non_2x/food-delivery-paper-bag-and-coffee-cup-hand-icon-lunch-linear-style-sign-for-mobile-concept-and-web-design-take-away-fast-food-simple-line-illustration-graphics-free-png.png"
            }
            alt="Take Away"
            className="h-32 w-32 object-cover mx-auto"
          />
          <div className="mt-4 w-full">
            <p className="text-2xl text-black font-bold">{designData.takeawaywrite || "Take Away"}</p>
          </div>
        </div>
      </div>

      {selectedOption && (
        <div className="absolute top-[180px] left-1/2 transform -translate-x-1/2 text-center">
          <div className="bg-green-100 backdrop-blur-sm rounded-2xl p-4 mb-4 border border-green-300">
            <p className="text-3xl text-black whitespace-nowrap font-semibold px-6 py-2 rounded-xl">
              Your Choose:{" "}
              <span className="text-green-700">
                {selectedOption === "drinkIn"
                  ? designData.drinkinwrite || "Drink In"
                  : designData.takeawaywrite || "Take Away"}
              </span>
            </p>
          </div>
        </div>
      )}

      <div className="absolute bottom-[80px] left-1/2 transform -translate-x-1/2 text-center">
        <button
          className={`px-12 py-6 text-xl font-bold whitespace-nowrap rounded-2xl transition-all duration-300 w-[320px] text-center ${
            selectedOption
              ? "bg-gradient-to-r from-green-500 to-green-700 text-white shadow-2xl animate-pulse"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          {selectedOption ? "NEXT →" : "Please Select Option"}
        </button>
      </div>
    </div>
  );
};

const MenuPagePreview = ({ designData }) => {
  const [activeCategory, setActiveCategory] = useState("coffees");

  const menuItems = [
    {
      id: "coffees",
      label: "Coffees",
      icon: "https://images.unsplash.com/photo-1561047029-3000c68339ca?w=150&h=150&fit=crop&crop=center",
    },
    {
      id: "cold-drinks",
      label: "Cold Drinks",
      icon: "https://images.unsplash.com/photo-1621506289937-a8e4df240d0b?w=150&h=150&fit=crop&crop=center",
    },
    {
      id: "special-offers",
      label: "Special Offers",
      icon: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=150&h=150&fit=crop&crop=center",
    },
    {
      id: "sandwiches",
      label: "Sandwiches",
      icon: "https://images.unsplash.com/photo-1561758033-d89a9ad46330?w=150&h=150&fit=crop&crop=center",
    },
    {
      id: "breakfast",
      label: "Breakfast",
      icon: "https://images.unsplash.com/photo-1551782450-17144efb9c50?w=150&h=150&fit=crop&crop=center",
    },
    {
      id: "desserts",
      label: "Desserts",
      icon: "https://images.unsplash.com/photo-1551024506-0bccd828d307?w=150&h=150&fit=crop&crop=center",
    },
  ];

  const sampleProducts = [
    {
      id: 1,
      name: "Espresso",
      price: "£4.50",
      image: "https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?w=400&h=400&fit=crop&crop=center",
      desc: "Strong and rich espresso",
    },
    {
      id: 2,
      name: "Cappuccino",
      price: "£5.50",
      image: "https://images.unsplash.com/photo-1572442388796-11668a67e53d?w=400&h=400&fit=crop&crop=center",
      desc: "Creamy cappuccino",
    },
    {
      id: 3,
      name: "Latte",
      price: "£5.00",
      image: "https://images.unsplash.com/photo-1561047029-3000c68339ca?w=400&h=400&fit=crop&crop=center",
      desc: "Smooth latte",
    },
    {
      id: 4,
      name: "Americano",
      price: "£4.00",
      image: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=400&h=400&fit=crop&crop=center",
      desc: "Classic americano",
    },
    {
      id: 5,
      name: "Mocha",
      price: "£6.00",
      image:
        "https://media.istockphoto.com/id/157675911/photo/cappuccino-topped-with-swirls-of-chocolate-sauce.jpg?s=612x612&w=0&k=20&c=606NMYMjVnTmpSnJI537_IjW3lqfNJaH7Lc9Qg0BXPU=",
      desc: "Chocolate mocha",
    },
    {
      id: 6,
      name: "Macchiato",
      price: "£5.50",
      image: "https://images.unsplash.com/photo-1485808191679-5f86510681a2?w=400&h=400&fit=crop&crop=center",
      desc: "Caramel macchiato",
    },
  ];

  const getHeaderBackground = () => ({ background: getHeaderColor(designData.menupagephotorenk) });
  const getCategoryGradient = () => getColorClass(designData.menukategorirenk);

  return (
    <div className="h-[960px] w-[540px] bg-gradient-to-br from-gray-50 via-white to-emerald-50 relative overflow-hidden">
      <div className="relative h-40 overflow-hidden" style={getHeaderBackground()}>
        <div className="relative h-full flex flex-col items-center justify-center text-white px-6">
          <h1 className="text-3xl font-bold mb-2">{designData.menupageyazi || "CAFE COLOMBIA"}</h1>
        </div>
      </div>

      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200 shadow-lg">
        <div className="flex overflow-x-auto px-4 py-3 space-x-2 no-scrollbar">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveCategory(item.id)}
              className={`group flex-shrink-0 flex items-center space-x-3 px-4 py-3 rounded-xl font-semibold transition-all duration-300 ${
                activeCategory === item.id
                  ? `bg-gradient-to-r ${getCategoryGradient()} text-white shadow-lg scale-105`
                  : "bg-gray-50 text-gray-700 hover:bg-gray-100"
              }`}
            >
              <div
                className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${
                  activeCategory === item.id ? "border-white/50" : "border-gray-200"
                }`}
              >
                <img alt={item.label} className="w-full h-full object-cover" src={item.icon} />
              </div>
              <span className="text-sm flex-1 text-left font-medium whitespace-nowrap">{item.label}</span>
              {activeCategory === item.id && <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>}
            </button>
          ))}
        </div>
      </div>

      <div className="px-6 py-6 overflow-y-auto" style={{ height: "calc(100% - 160px - 76px)" }}>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {menuItems.find((item) => item.id === activeCategory)?.label}
          </h2>
        </div>

        <div className="grid grid-cols-2 gap-4 pb-28">
          {sampleProducts.map((product) => (
            <div
              key={product.id}
              className="group relative bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-1 cursor-pointer"
            >
              <div className="relative h-32 overflow-hidden">
                <img
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  src={product.image}
                />
                <div className="absolute top-2 right-2 bg-white text-gray-900 px-3 py-2 rounded-full shadow-lg font-bold text-sm">
                  {product.price}
                </div>
              </div>
              <div className="p-3">
                <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-emerald-600 transition-colors line-clamp-1">
                  {product.name}
                </h3>
                <p className="text-gray-600 text-xs leading-relaxed line-clamp-2">{product.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-gray-200 shadow-2xl">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <button className="px-6 py-3 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-xl font-bold text-base hover:shadow-xl hover:scale-105 transition-all duration-300 shadow-lg">
              Cancel Order
            </button>

            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl px-4 py-2 border-2 border-emerald-200 shadow-lg">
                <div className="text-center">
                  <div className="text-lg font-bold text-emerald-700">$0.00</div>
                  <div className="text-sm text-emerald-600 font-medium">Cart</div>
                </div>
              </div>

              <button
                className={`px-8 py-3 bg-gradient-to-r ${getColorClass(
                  designData.butonrenk
                )} text-white rounded-xl font-bold text-base hover:shadow-xl hover:scale-105 transition-all duration-300 shadow-lg`}
              >
                Order Now →
              </button>

              <button className="px-8 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-bold text-base hover:shadow-xl hover:scale-105 transition-all duration-300 shadow-lg">
                Proceed Order →
              </button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

const ProductPagePreview = ({ designData, productList = [], itemListData = [], onFieldChange }) => {
  const [activeCategory, setActiveCategory] = useState("home");

  const advertisementImage = designData.reklamphoto || "";

  // Kategori fotoğrafları için designData'dan al
  const featuredCategoryDetails = [];
  for (let i = 1; i <= 4; i++) {
    const categoryLabel = designData[`category${i}`];
    
    // Kategori listesinden icon'ı bul
    let categoryImage = "";
    if (categoryLabel) {
      const foundCategory = itemListData.find(item => item.label === categoryLabel);
      if (foundCategory && foundCategory.icon) {
        categoryImage = foundCategory.icon;
      }
    }
    
    if (categoryLabel) {
      featuredCategoryDetails.push({
        label: categoryLabel,
        image: categoryImage || `https://via.placeholder.com/48x48?text=Kategori+${i}`,
      });
    }
  }

  const selectedProducts = [];
  const usedProductCodes = new Set();

  for (let i = 1; i <= 6; i++) {
    const productCode = designData[`urun${i}`];
    if (productCode && !usedProductCodes.has(productCode)) {
      const product = productList.find((p) => p.uniqcode === productCode);
      if (product) {
        selectedProducts.push(product);
        usedProductCodes.add(productCode);
      }
    }
  }

  const getHeaderBackground = () => ({ background: getHeaderColor(designData.menupagephotorenk) });
  const getCategoryGradient = () => getColorClass(designData.menukategorirenk);

  const menuItems = [
    { id: "home", label: "Ana Sayfa", icon: "🏠" },
    { id: "category1", label: "Kategori 1", icon: "🍔" },
    { id: "category2", label: "Kategori 2", icon: "🍕" },
    { id: "category3", label: "Kategori 3", icon: "🥗" },
    { id: "category4", label: "Kategori 4", icon: "🍰" },
    { id: "category5", label: "Kategori 5", icon: "🥤" },
  ];

  const removeAdvertisement = () => {
    if (typeof onFieldChange === "function") {
      onFieldChange("reklamphoto", "");
    }
  };

  const hasEmptyProductFields = () => {
    for (let i = 1; i <= 6; i++) {
      if (!designData[`urun${i}`]) return true;
    }
    return false;
  };

  return (
    <div className="h-[960px] w-[540px] bg-gradient-to-br from-gray-50 via-white to-emerald-50 relative overflow-hidden">
      <div className="relative h-32 overflow-hidden" style={getHeaderBackground()}>
        <div className="relative h-full flex flex-col items-center justify-center text-white px-6">
          <h1 className="text-2xl font-bold mb-2">{designData.baslikyazisi || "ÖNE ÇIKAN ÜRÜNLER"}</h1>
        </div>
      </div>

      <div className="flex h-[calc(960px-8rem)]">
        <div className="w-36 bg-white/80 backdrop-blur-sm h-full border-r border-gray-200 shadow-lg overflow-y-auto">
          <div className="p-1 space-y-1">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveCategory(item.id)}
                className={`group w-full flex items-center space-x-2 px-2 py-2 rounded-lg font-semibold transition-all duration-300 ${
                  activeCategory === item.id
                    ? `bg-gradient-to-r ${getCategoryGradient()} text-white shadow-md scale-105`
                    : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                }`}
              >
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-lg">{item.icon}</div>
                <span className="text-xs flex-1 text-left font-medium leading-tight">{item.label}</span>
                {activeCategory === item.id && <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 px-2 py-3 overflow-y-auto">
          {featuredCategoryDetails.length > 0 && (
            <div className="mb-4">
              <h2 className="text-lg font-bold text-gray-900 mb-2">Öne Çıkan Kategoriler</h2>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {featuredCategoryDetails.slice(0, 4).map((category, index) => (
                  <div
                    key={index}
                    className="group relative bg-white rounded-md overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 cursor-pointer border border-gray-200"
                  >
                    <div className="flex items-center p-2 bg-white">
                      <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-md mr-3 overflow-hidden flex-shrink-0">
                        <img
                          src={category.image}
                          alt={category.label}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = "https://via.placeholder.com/48x48?text=📦";
                          }}
                        />
                      </div>
                      <span className="text-gray-900 text-base font-bold text-left flex-1">
                        {category.label || `Kategori ${index + 1}`}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mb-4">
            <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 overflow-hidden">
              {advertisementImage ? (
                <div className="relative">
                  <img
                    src={advertisementImage}
                    alt="Reklam"
                    className="w-full h-[88px] object-cover"
                    onError={(e) => {
                      e.currentTarget.src = "https://via.placeholder.com/380x88?text=Reklam+Alan%C4%B1";
                    }}
                  />
                  <button
                    onClick={removeAdvertisement}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors"
                    title="Reklamı Kaldır"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="h-[88px] flex flex-col items-center justify-center text-gray-400">
                  <svg className="w-10 h-10 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-sm">Reklam fotoğrafını ayarlardan yükleyin</p>
                  <p className="text-xs mt-0.5">Yüklediğiniz fotoğraf burada görünecek</p>
                </div>
              )}
            </div>
          </div>

          <div className="mb-3">
            <h2 className="text-lg font-bold text-gray-900 mb-1">
              {activeCategory === "home"
                ? "Öne Çıkan Ürünler"
                : menuItems.find((item) => item.id === activeCategory)?.label}
            </h2>
            <p className="text-xs text-gray-500 mb-2">Seçilen ürünler: {selectedProducts.length}/6 (Benzersiz ürünler)</p>
          </div>

          {selectedProducts.length > 0 ? (
            <div className="grid grid-cols-3 gap-2 pb-20">
              {selectedProducts.map((product, index) => (
                <div
                  key={product.uniqcode || index}
                  className="group relative bg-white rounded-md overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 cursor-pointer"
                >
                  <div className="relative h-28 overflow-hidden">
                    <img
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      src={product.image}
                      onError={(e) => {
                        e.currentTarget.src = "https://via.placeholder.com/138x115?text=%C3%9Cr%C3%BCn";
                      }}
                    />
                    <div className="absolute top-1.5 right-1.5 bg-white text-gray-900 px-2 py-0.5 rounded-full shadow text-xs font-bold">
                      {product.price}
                    </div>
                  </div>
                  <div className="p-2">
                    <h3 className="text-xs font-bold text-gray-900 mb-0.5 group-hover:text-emerald-600 transition-colors line-clamp-1">
                      {product.name}
                    </h3>
                    <p className="text-gray-600 text-xs leading-tight line-clamp-2">{product.desc || "Ürün açıklaması"}</p>
                    <div className="mt-1">
                      {[1, 2, 3, 4, 5, 6].map(
                        (i) =>
                          designData[`urun${i}`] === product.uniqcode && (
                            <span
                              key={i}
                              className="inline-block bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5 rounded mr-1"
                            >
                              Ürün {i}
                            </span>
                          )
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="text-gray-400 text-3xl mb-2">📦</div>
              <h3 className="text-base font-semibold text-gray-600 mb-1">Henüz ürün seçilmemiş</h3>
              <p className="text-gray-500 text-xs">Sağ taraftan ürün seçebilirsiniz</p>
            </div>
          )}

          {hasEmptyProductFields() && selectedProducts.length < 6 && (
            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
              ⚠️ Bazı ürün alanları boş veya aynı ürün seçilmiş. Her ürün alanına farklı bir ürün seçin.
            </div>
          )}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-gray-200 shadow-md">
        <div className="px-3 py-2">
          <div className="flex items-center justify-between">
            <button className="px-3 py-1.5 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-md font-bold text-xs hover:shadow hover:scale-105 transition-all duration-200 shadow-sm">
              Cancel
            </button>

            <div className="flex items-center space-x-1.5">
              <div className="flex items-center space-x-1.5 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-md px-2 py-1 border border-emerald-200 shadow-sm">
                <div className="text-center">
                  <div className="text-sm font-bold text-emerald-700">$0.00</div>
                  <div className="text-xs text-emerald-600 font-medium">Cart</div>
                </div>
              </div>

              <button
                className={`px-4 py-1.5 bg-gradient-to-r ${getColorClass(
                  designData.butonrenk
                )} text-white rounded-md font-bold text-xs hover:shadow hover:scale-105 transition-all duration-200 shadow-sm`}
              >
                Order Now →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FileUploadComponent = ({
  field,
  currentValue,
  onUploadComplete,
  label,
  accept = "image/*",
  enableCrop = false,
  cropAspect = 9 / 16,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [cropOpen, setCropOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageSrc, setImageSrc] = useState("");

  const openCropForFile = (file) => {
    const url = URL.createObjectURL(file);
    setSelectedFile(file);
    setImageSrc(url);
    setCropOpen(true);
  };

  const cleanupObjectUrl = () => {
    if (imageSrc) {
      try {
        URL.revokeObjectURL(imageSrc);
      } catch (_) {}
    }
  };

  useEffect(() => {
    return () => cleanupObjectUrl();
  }, []);

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      alert("Dosya boyutu 50MB'dan küçük olmalıdır");
      return;
    }

    if (enableCrop) {
      openCropForFile(file);
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const timestamp = Date.now();
      const ext = file.name.split(".").pop() || "jpg";
      const fileName = `${field}_${timestamp}.${ext}`;

      const downloadURL = await uploadFileToR2ViaBackend(file, fileName, field);
      onUploadComplete(field, downloadURL);

      setUploadProgress(100);
      setTimeout(() => {
        setUploadProgress(0);
        setUploading(false);
      }, 600);
    } catch (error) {
      alert(`Dosya yükleme hatası: ${error.message}`);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCropConfirm = async (croppedAreaPixels) => {
    if (!selectedFile || !imageSrc || !croppedAreaPixels) {
      setCropOpen(false);
      cleanupObjectUrl();
      setSelectedFile(null);
      setImageSrc("");
      return;
    }

    try {
      setCropOpen(false);
      setUploading(true);
      setUploadProgress(0);

      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels);

      const timestamp = Date.now();
      const fileName = `${field}_${timestamp}.jpg`;
      const croppedFile = new File([blob], fileName, { type: "image/jpeg" });

      const downloadURL = await uploadFileToR2ViaBackend(croppedFile, fileName, field);
      onUploadComplete(field, downloadURL);

      setUploadProgress(100);
      setTimeout(() => {
        setUploadProgress(0);
        setUploading(false);
      }, 600);
    } catch (error) {
      alert(`Crop/Upload hatası: ${error.message}`);
      setUploading(false);
      setUploadProgress(0);
    } finally {
      cleanupObjectUrl();
      setSelectedFile(null);
      setImageSrc("");
    }
  };

  const handleCropClose = () => {
    setCropOpen(false);
    cleanupObjectUrl();
    setSelectedFile(null);
    setImageSrc("");
  };

  return (
    <div className="mb-4">
      <CropModal
        isOpen={cropOpen}
        imageSrc={imageSrc}
        aspect={cropAspect}
        onClose={handleCropClose}
        onConfirm={handleCropConfirm}
        title={`${label} - Crop`}
      />

      {label && <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>}

      <div className="mb-3">
        {currentValue ? (
          <>
            <p className="text-xs text-gray-500 mb-1">Mevcut görsel:</p>
            <img
              src={currentValue}
              alt={label}
              className="h-24 w-auto rounded-lg border border-gray-300 object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </>
        ) : (
          <p className="text-xs text-gray-500 mb-1">Henüz fotoğraf yüklenmemiş</p>
        )}
      </div>

      <div className="flex items-center space-x-2">
        <label className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg cursor-pointer text-sm transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed">
          {uploading ? "Yükleniyor..." : enableCrop ? "Fotoğraf Seç (Crop)" : "Fotoğraf Seç"}
          <input type="file" className="hidden" accept={accept} onChange={handleFileUpload} disabled={uploading} />
        </label>

        {uploading && (
          <div className="flex-1 bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-500 mt-1">Desteklenen formatlar: JPG, JPEG, PNG, GIF, WebP (Max. 50MB)</p>

      <input
        type="text"
        value={currentValue || ""}
        onChange={(e) => onUploadComplete(field, e.target.value)}
        className="w-full mt-2 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        placeholder={`${label} URL'si`}
      />
    </div>
  );
};

const DesignPanel = () => {
  const defaultDesignData = {
    applogo: "",
    appvideo: "",
    appyazi: "",
    appaltyazi: "",
    drinkin: "",
    drinkinwrite: "Drink In",
    takeaway: "",
    takeawaywrite: "Take Away",
    menupagephotorenk: "",
    menukategorirenk: "",
    butonrenk: "",
    menupageyazi: "",
    openhour: "8:00 AM - 10:00 PM",
    addinfo: "Since 1995",
    firstemo: "coffee",
    firstemowrite: "Coffee",
    secondemo: "soda",
    secondemowrite: "Drinks",
    thirdemo: "cake",
    thirdemowrite: "Desserts",
    productpageyazi: "ÖNE ÇIKAN ÜRÜNLER",
    category1: "",
    category2: "",
    category3: "",
    category4: "",
    category1resim: "",
    category2resim: "",
    category3resim: "",
    category4resim: "",
    urun1: "",
    urun2: "",
    urun3: "",
    urun4: "",
    urun5: "",
    urun6: "",
    reklamphoto: "",
    baslikyazisi: "ÖNE ÇIKAN ÜRÜNLER",
  };

  const [designData, setDesignData] = useState(defaultDesignData);
  const [initialDesignData, setInitialDesignData] = useState(defaultDesignData);
  const [activePreview, setActivePreview] = useState("homepage");
  const [initialLoading, setInitialLoading] = useState(true);
  const [productList, setProductList] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [itemListData, setItemListData] = useState([]);
  const [itemListLoading, setItemListLoading] = useState(false);

  const [showSaveLoadingModal, setShowSaveLoadingModal] = useState(false);
  const [showSaveSuccessModal, setShowSaveSuccessModal] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

  const colorOptions = [
    "red",
    "blue",
    "green",
    "yellow",
    "orange",
    "purple",
    "pink",
    "indigo",
    "teal",
    "cyan",
    "amber",
    "emerald",
    "lime",
    "rose",
    "fuchsia",
    "violet",
    "sunset",
    "ocean",
    "forest",
    "berry",
    "fire",
    "sky",
  ];

  const headerColorOptions = ["red", "blue", "green", "yellow", "orange", "purple", "pink", "indigo", "teal", "cyan", "emerald"];

  const hasUnsavedChanges = useCallback(() => {
    if (!initialDesignData || Object.keys(initialDesignData).length === 0) return false;

    const keys = Object.keys(designData);
    for (let key of keys) {
      if (designData[key] !== initialDesignData[key]) return true;
    }
    return false;
  }, [designData, initialDesignData]);

  const fetchItemList = useCallback(async () => {
    if (!checkAuthentication()) return;

    try {
      setItemListLoading(true);
      const card = getAuthCard();

      const response = await makeApiRequest(`${API_BASE_URL}/getcategories`, {
        method: "POST",
        body: JSON.stringify({ card }),
      });

      if (response.success && response.categories) {
        // Her kategori için label ve icon bilgilerini al
        const categoriesWithIcon = response.categories.map(category => ({
          id: category.id,
          label: category.label,
          icon: category.icon || category.image || "" // icon veya image field'ını al
        }));
        
        setItemListData(categoriesWithIcon);
      }
    } catch (error) {
      console.error("Itemlist kategorileri yüklenirken hata:", error);
    } finally {
      setItemListLoading(false);
    }
  }, []);

  const fetchAllProducts = useCallback(async () => {
    if (!checkAuthentication()) return;

    try {
      setProductsLoading(true);
      const card = getAuthCard();

      const response = await makeApiRequest(`${API_BASE_URL}/getallproductsbycard`, {
        method: "POST",
        body: JSON.stringify({ card }),
      });

      if (response.success && response.products) {
        setProductList(response.products);
      }
    } catch (error) {
      console.error("Ürünler yüklenirken hata:", error);
    } finally {
      setProductsLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchDesignData = async () => {
      try {
        setInitialLoading(true);
        if (!checkAuthentication()) return;

        const card = getAuthCard();

        const response = await makeApiRequest(`${API_BASE_URL}/getdesignhomepage`, {
          method: "POST",
          body: JSON.stringify({ card }),
        });

        if (response.success && response.designData) {
          const newData = {
            ...defaultDesignData,
            ...response.designData,
            category1: response.designData.category1 || "",
            category2: response.designData.category2 || "",
            category3: response.designData.category3 || "",
            category4: response.designData.category4 || "",
            category1resim: response.designData.category1resim || "",
            category2resim: response.designData.category2resim || "",
            category3resim: response.designData.category3resim || "",
            category4resim: response.designData.category4resim || "",
            urun1: response.designData.urun1 || "",
            urun2: response.designData.urun2 || "",
            urun3: response.designData.urun3 || "",
            urun4: response.designData.urun4 || "",
            urun5: response.designData.urun5 || "",
            urun6: response.designData.urun6 || "",
            reklamphoto: response.designData.reklamphoto || "",
            baslikyazisi: response.designData.baslikyazisi || "ÖNE ÇIKAN ÜRÜNLER",
          };
          setDesignData(newData);
          setInitialDesignData(newData);
        } else {
          setDesignData(defaultDesignData);
          setInitialDesignData(defaultDesignData);
        }
      } catch (error) {
        const saved = localStorage.getItem("designData");
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            setDesignData(parsed);
            setInitialDesignData(parsed);
          } catch (_) {}
        }
      } finally {
        setInitialLoading(false);
      }
    };

    fetchDesignData();
    fetchItemList();
    fetchAllProducts();
  }, [fetchItemList, fetchAllProducts]);

  const saveDesignToBackend = async () => {
    try {
      setShowSaveLoadingModal(true);
      if (!checkAuthentication()) {
        setShowSaveLoadingModal(false);
        return;
      }

      const card = getAuthCard();
      const updatedDesignData = {
        ...designData,
        category1: designData.category1 || "",
        category2: designData.category2 || "",
        category3: designData.category3 || "",
        category4: designData.category4 || "",
        category1resim: designData.category1resim || "",
        category2resim: designData.category2resim || "",
        category3resim: designData.category3resim || "",
        category4resim: designData.category4resim || "",
        urun1: designData.urun1 || "",
        urun2: designData.urun2 || "",
        urun3: designData.urun3 || "",
        urun4: designData.urun4 || "",
        urun5: designData.urun5 || "",
        urun6: designData.urun6 || "",
        reklamphoto: designData.reklamphoto || "",
        baslikyazisi: designData.baslikyazisi || "ÖNE ÇIKAN ÜRÜNLER",
      };

      const response = await makeApiRequest(`${API_BASE_URL}/savedesign`, {
        method: "POST",
        body: JSON.stringify({ designData: updatedDesignData, card }),
      });

      if (response.success) {
        localStorage.setItem("designData", JSON.stringify(updatedDesignData));
        setInitialDesignData(updatedDesignData);
        setShowSaveLoadingModal(false);
        setShowSaveSuccessModal(true);
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      if (!(error.message || "").includes("Authentication failed")) {
        alert(`Tasarım güncelleme hatası: ${error.message}`);
      }
      setShowSaveLoadingModal(false);
    }
  };

  const handleInputChange = (field, value) => {
    const newData = { ...designData, [field]: value };
    setDesignData(newData);
    localStorage.setItem("designData", JSON.stringify(newData));
  };

  const handleFileUploadComplete = (field, downloadURL) => {
    handleInputChange(field, downloadURL);
  };

  const handleColorChange = (colorName) => handleInputChange("butonrenk", colorName);
  const handleHeaderColorChange = (colorName) => handleInputChange("menupagephotorenk", colorName);
  const handleCategoryColorChange = (colorName) => handleInputChange("menukategorirenk", colorName);

  const handleProductChange = (index, productCode) => {
    const fieldName = `urun${index}`;
    handleInputChange(fieldName, productCode);
  };

  const handleCategoryChange = (index, categoryValue) => {
    const fieldName = `category${index}`;
    
    // Kategori icon'ını otomatik olarak bul
    let categoryIcon = "";
    if (categoryValue) {
      const foundCategory = itemListData.find(item => item.label === categoryValue);
      if (foundCategory && foundCategory.icon) {
        categoryIcon = foundCategory.icon;
      }
    }
    
    // Hem kategori label'ını hem de icon'ını kaydet
    const newData = { 
      ...designData, 
      [fieldName]: categoryValue,
      [`category${index}resim`]: categoryIcon
    };
    
    setDesignData(newData);
    localStorage.setItem("designData", JSON.stringify(newData));
  };

  const handleBackToMerchant = () => {
    if (hasUnsavedChanges()) {
      setShowUnsavedChangesModal(true);
    } else {
      window.location.href = "/editday";
    }
  };

  const handleUnsavedChangesConfirm = () => {
    setShowUnsavedChangesModal(false);
    window.location.href = "/editday";
  };

  const handleUnsavedChangesCancel = () => setShowUnsavedChangesModal(false);
  const handleSuccessModalClose = () => setShowSaveSuccessModal(false);

  if (initialLoading) return <LoadingSpinner message="LOADING DESIGN DATA..." />;

  const renderSettingsContent = () => {
    switch (activePreview) {
      case "homepage":
        return (
          <div className="space-y-6">
            <h3 className="font-medium text-gray-800">Home Page Settings</h3>

            <FileUploadComponent
              field="applogo"
              currentValue={designData.applogo}
              onUploadComplete={handleFileUploadComplete}
              label="App Logo"
              accept="image/*"
            />

            <FileUploadComponent
              field="appvideo"
              currentValue={designData.appvideo}
              onUploadComplete={handleFileUploadComplete}
              label="Background Photo"
              accept="image/*"
              enableCrop={true}
              cropAspect={9 / 16}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Main Text</label>
              <input
                type="text"
                value={designData.appyazi}
                onChange={(e) => handleInputChange("appyazi", e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Main heading text"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle Text</label>
              <input
                type="text"
                value={designData.appaltyazi}
                onChange={(e) => handleInputChange("appaltyazi", e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Subtitle text"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Order Now Button Color</label>
              <div className="grid grid-cols-4 gap-2 mb-2">
                {colorOptions.map((color, index) => (
                  <button
                    key={index}
                    onClick={() => handleColorChange(color)}
                    className={`h-8 rounded-lg border-2 transition-all bg-gradient-to-r ${getColorClass(color)} ${
                      designData.butonrenk === color
                        ? "border-blue-500 scale-110 shadow-lg"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                    title={color}
                  />
                ))}
              </div>
              <div className="text-xs text-gray-500 mb-2">
                Seçili renk: <strong>{designData.butonrenk || "default"}</strong>
              </div>
              <div className="mt-2">
                <button
                  onClick={() => handleColorChange("")}
                  className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm hover:bg-gray-200 transition-colors"
                >
                  Reset to Default
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Quick Access Buttons</label>
              <div className="space-y-3">
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-600">First Button</label>
                  <div className="flex space-x-2 items-center">
                    <select
                      value={designData.firstemo}
                      onChange={(e) => handleInputChange("firstemo", e.target.value)}
                      className="w-24 border border-gray-300 rounded-lg px-2 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      {emojiOptions.map((option, index) => (
                        <option key={index} value={option.code}>
                          {option.emoji} {option.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={designData.firstemowrite}
                      onChange={(e) => handleInputChange("firstemowrite", e.target.value)}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Button label"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-600">Second Button</label>
                  <div className="flex space-x-2 items-center">
                    <select
                      value={designData.secondemo}
                      onChange={(e) => handleInputChange("secondemo", e.target.value)}
                      className="w-24 border border-gray-300 rounded-lg px-2 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      {emojiOptions.map((option, index) => (
                        <option key={index} value={option.code}>
                          {option.emoji} {option.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={designData.secondemowrite}
                      onChange={(e) => handleInputChange("secondemowrite", e.target.value)}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Button label"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-600">Third Button</label>
                  <div className="flex space-x-2 items-center">
                    <select
                      value={designData.thirdemo}
                      onChange={(e) => handleInputChange("thirdemo", e.target.value)}
                      className="w-24 border border-gray-300 rounded-lg px-2 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      {emojiOptions.map((option, index) => (
                        <option key={index} value={option.code}>
                          {option.emoji} {option.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={designData.thirdemowrite}
                      onChange={(e) => handleInputChange("thirdemowrite", e.target.value)}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Button label"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Opening Hours</label>
              <input
                type="text"
                value={designData.openhour}
                onChange={(e) => handleInputChange("openhour", e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="8:00 AM - 10:00 PM"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Additional Info</label>
              <input
                type="text"
                value={designData.addinfo}
                onChange={(e) => handleInputChange("addinfo", e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Since 1995"
              />
            </div>
          </div>
        );

      case "menupage":
        return (
          <div className="space-y-6">
            <h3 className="font-medium text-gray-800">Menu Page Settings</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Header Background Color</label>
              <div className="grid grid-cols-4 gap-2 mb-2">
                {headerColorOptions.map((color, index) => (
                  <button
                    key={index}
                    onClick={() => handleHeaderColorChange(color)}
                    className={`h-8 rounded-lg border-2 transition-all ${
                      designData.menupagephotorenk === color
                        ? "border-blue-500 scale-110 shadow-lg"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                    style={{ background: getHeaderColor(color) }}
                    title={color}
                  />
                ))}
              </div>
              <div className="text-xs text-gray-500 mb-2">
                Seçili renk: <strong>{designData.menupagephotorenk || "default"}</strong>
              </div>
              <div className="mt-2">
                <button
                  onClick={() => handleHeaderColorChange("")}
                  className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm hover:bg-gray-200 transition-colors"
                >
                  Reset to Default
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category Active Color</label>
              <div className="grid grid-cols-4 gap-2 mb-2">
                {colorOptions.map((color, index) => (
                  <button
                    key={index}
                    onClick={() => handleCategoryColorChange(color)}
                    className={`h-8 rounded-lg border-2 transition-all bg-gradient-to-r ${getColorClass(color)} ${
                      designData.menukategorirenk === color
                        ? "border-blue-500 scale-110 shadow-lg"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                    title={color}
                  />
                ))}
              </div>
              <div className="text-xs text-gray-500 mb-2">
                Seçili renk: <strong>{designData.menukategorirenk || "default"}</strong>
              </div>
              <div className="mt-2">
                <button
                  onClick={() => handleCategoryColorChange("")}
                  className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm hover:bg-gray-200 transition-colors"
                >
                  Reset to Default
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Header Text</label>
              <input
                type="text"
                value={designData.menupageyazi}
                onChange={(e) => handleInputChange("menupageyazi", e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Menu page header text"
              />
            </div>
          </div>
        );

      case "takeaway":
        return (
          <div className="space-y-4">
            <h3 className="font-medium text-gray-800">Take Away Page Settings</h3>

            <FileUploadComponent
              field="drinkin"
              currentValue={designData.drinkin}
              onUploadComplete={handleFileUploadComplete}
              label="Drink In Image"
              accept="image/*"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Drink In Text</label>
              <input
                type="text"
                value={designData.drinkinwrite}
                onChange={(e) => handleInputChange("drinkinwrite", e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Drink In"
              />
            </div>

            <FileUploadComponent
              field="takeaway"
              currentValue={designData.takeaway}
              onUploadComplete={handleFileUploadComplete}
              label="Take Away Image"
              accept="image/*"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Take Away Text</label>
              <input
                type="text"
                value={designData.takeawaywrite}
                onChange={(e) => handleInputChange("takeawaywrite", e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Take Away"
              />
            </div>
          </div>
        );

      case "productpage":
        return (
          <div className="space-y-6">
            <h3 className="font-medium text-gray-800">Ürün Sayfası Ayarları</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Başlık Yazısı</label>
              <input
                type="text"
                value={designData.baslikyazisi || ""}
                onChange={(e) => handleInputChange("baslikyazisi", e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="ÖNE ÇIKAN ÜRÜNLER"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                4 Öne Çıkan Kategori Seçin (Icon otomatik gelecek)
              </label>

              {itemListLoading ? (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  <p className="text-sm text-gray-500 mt-2">Kategoriler yükleniyor...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {[1, 2, 3, 4].map((index) => {
                    const categoryLabel = designData[`category${index}`] || "";
                    
                    // Kategori icon'ını bul
                    let iconPreview = "";
                    if (categoryLabel) {
                      const foundCategory = itemListData.find(item => item.label === categoryLabel);
                      iconPreview = foundCategory?.icon || "";
                    }

                    return (
                      <div key={index} className="space-y-3 p-4 border border-gray-200 rounded-lg">
                        <div className="space-y-1">
                          <label className="block text-xs font-medium text-gray-600">
                            Kategori {index}
                          </label>
                          <select
                            value={categoryLabel}
                            onChange={(e) => handleCategoryChange(index, e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          >
                            <option value="">-- Kategori Seçin --</option>
                            {itemListData.map((category) => (
                              <option key={category.id} value={category.label}>
                                {category.label} 
                                {category.icon && " (icon var)"}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="space-y-1">
                          <label className="block text-xs font-medium text-gray-600">
                            Kategori {index} Icon'ı (Otomatik alınır)
                          </label>
                          
                          <div className="mt-2">
                            <p className="text-xs text-gray-500 mb-1">Icon Önizleme:</p>
                            <div className="flex items-center space-x-2">
                              <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                                {iconPreview ? (
                                  <img
                                    src={iconPreview}
                                    alt={`Kategori ${index} Icon`}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.src = "https://via.placeholder.com/48x48?text=📦";
                                    }}
                                  />
                                ) : (
                                  <span className="text-2xl">📦</span>
                                )}
                              </div>
                              <div className="text-xs">
                                <p className="font-medium">{categoryLabel || "Kategori seçilmemiş"}</p>
                                <p className="text-gray-500">
                                  {iconPreview ? 
                                    "Icon mevcut" : 
                                    "Icon bulunamadı"}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                6 Ürün Seçin (collection: iteminfo, field: card = localStorage card)
              </label>

              {productsLoading ? (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  <p className="text-sm text-gray-500 mt-2">Ürünler yükleniyor...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  {[1, 2, 3, 4, 5, 6].map((index) => (
                    <div key={index} className="space-y-1">
                      <label className="block text-xs font-medium text-gray-600">Ürün {index}</label>
                      <select
                        value={designData[`urun${index}`] || ""}
                        onChange={(e) => handleProductChange(index, e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="">-- Ürün Seçin --</option>
                        {productList.map((product) => (
                          <option key={product.uniqcode} value={product.uniqcode}>
                            {product.name} - {product.price}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <FileUploadComponent
                field="reklamphoto"
                currentValue={designData.reklamphoto || ""}
                onUploadComplete={handleFileUploadComplete}
                label="Reklam Fotoğrafı"
                accept="image/*"
                enableCrop={true}
                cropAspect={AD_BANNER_ASPECT}
              />
              <p className="text-xs text-gray-500 mt-1">Reklam fotoğrafı, öne çıkan kategorilerin altında gösterilecektir.</p>
            </div>
          </div>
        );

      default:
        return <div>Select a page to configure settings</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {showSaveLoadingModal && <SaveLoadingModal />}
      {showSaveSuccessModal && <SaveSuccessModal onClose={handleSuccessModalClose} />}
      {showUnsavedChangesModal && (
        <UnsavedChangesModal onConfirm={handleUnsavedChangesConfirm} onCancel={handleUnsavedChangesCancel} />
      )}

      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <h1 className="text-2xl font-bold text-gray-900">Design Panel</h1>
            <div className="flex space-x-4">
              <button
                onClick={handleBackToMerchant}
                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
              >
                Back to Merchant Panel
              </button>
              <button
                onClick={saveDesignToBackend}
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
              >
                Save to Backend
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          <div className="w-80 bg-white rounded-lg shadow-sm border p-6 h-fit">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Design Settings</h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preview Section</label>
                <select
                  value={activePreview}
                  onChange={(e) => setActivePreview(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="homepage">Home Page</option>
                  <option value="menupage">Menu Page</option>
                  <option value="takeaway">Take Away Page</option>
                  <option value="productpage">Ürün Sayfası</option>
                </select>
              </div>

              {renderSettingsContent()}
            </div>
          </div>

          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Live Preview (540x960)</h2>
              <div className="border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-100 flex justify-center items-center p-4 min-h-[500px]">
                <div
                  className={`border-4 border-blue-500 rounded-lg overflow-hidden origin-top ${
                    activePreview === "productpage" ? "scale-110" : "scale-75"
                  }`}
                >
                  {activePreview === "homepage" && <HomePagePreview designData={designData} />}
                  {activePreview === "menupage" && <MenuPagePreview designData={designData} />}
                  {activePreview === "takeaway" && <TakeawayPagePreview designData={designData} />}
                  {activePreview === "productpage" && (
                    <ProductPagePreview
                      designData={designData}
                      productList={productList}
                      itemListData={itemListData}
                      onFieldChange={handleInputChange}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesignPanel;